import React from "react";
import { useTranslation } from "react-i18next";
import {
  LOST_DEAL,
  WON_DEAL,
  colorsByStatus,
  wonOrLostType
} from "@/pages/deals/components/KanbanBoard/KanbanBoard";
import { cn, getHexColor } from "@/utils/utils";
import {
  Droppable,
  DroppableProvided,
  DroppableStateSnapshot
} from "@hello-pangea/dnd";
import { Check, Close } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import { useSidebarState } from "@/services/context/SidebarContext";
import { map } from "lodash";

interface Props {
  isDragging: boolean;
  sourceColumnIndex: number | null;
}

const DragDropAreas: React.FC<Props> = ({ isDragging, sourceColumnIndex }) => {
  const { t } = useTranslation();
  const { isSidebarOpen } = useSidebarState();
  const renderArea = (id: wonOrLostType) => {
    const wonDisabled = id === WON_DEAL && sourceColumnIndex === 0;
    return (
      <Droppable droppableId={id} key={id} isDropDisabled={wonDisabled}>
        {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => {
          const getBackground = () => {
            const getGradient = (colorVar: string) => {
              const hexColor = getHexColor(colorVar);
              return `linear-gradient(to top, ${hexColor}66, #151D2C00)`;
            };
            if (wonDisabled) {
              return getGradient(colorsByStatus.disabled);
            }
            return getGradient(colorsByStatus[id]);
          };

          const getColor = () => {
            return getHexColor(
              wonDisabled ? colorsByStatus.disabled : colorsByStatus[id]
            );
          };

          const getArcGradientId = () =>
            `gradient-${id}-${wonDisabled ? "disabled" : "enabled"}`;

          return (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={cn(
                "w-96 relative transition-all duration-200 h-full"
              )}
            >
              <div className="flex w-96 h-full">
                {id === LOST_DEAL && (
                  <div
                    id="dropareaarc"
                    className="w-32 min-h-[50px] transition-all duration-200"
                  >
                    <svg
                      width="100%"
                      height="100%"
                      viewBox="0 0 74 60"
                      fill="none"
                      preserveAspectRatio="none"
                    >
                      <defs>
                        <linearGradient
                          id={getArcGradientId()}
                          x1="37"
                          y1="59"
                          x2="37"
                          y2="0"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop
                            offset="0"
                            stopColor={getColor()}
                            stopOpacity="0.4"
                          />
                          <stop
                            offset="1"
                            stopColor="#151D2C"
                            stopOpacity="0.36"
                          />
                        </linearGradient>
                      </defs>
                      <path
                        d="M0 0H74V59C63 39.7 46.8 24.1 27.1 13.9L0 0Z"
                        fill={`url(#${getArcGradientId()})`}
                      />
                    </svg>
                  </div>
                )}

                <div
                  id="dropareabox"
                  className="w-64 min-h-[75px] flex items-center justify-center transition-all duration-200 relative"
                  style={{ backgroundImage: getBackground() }}
                >
                  <div className="text-center font-semibold fixed bottom-10 flex gap-2 items-center justify-center">
                    {id === LOST_DEAL ? (
                      <IconContainer
                        as="span"
                        size="md"
                        color="charts-negative"
                        nativeStroke
                        className="scale-150"
                      >
                        <Close />
                      </IconContainer>
                    ) : (
                      <IconContainer
                        as="span"
                        size="md"
                        color={wonDisabled ? "tertiary" : "cta"}
                        nativeStroke
                      >
                        <Check />
                      </IconContainer>
                    )}
                    <Text
                      as="div"
                      variant={
                        wonDisabled
                          ? "lgBodySecondary"
                          : snapshot.isDraggingOver
                            ? "label"
                            : "body"
                      }
                    >
                      {t(`deals.${id}`)}
                    </Text>
                  </div>
                </div>

                {id === WON_DEAL && (
                  <div
                    id="dropareaarc"
                    className="w-32 min-h-[50px] transition-all duration-200"
                  >
                    <svg
                      width="100%"
                      height="100%"
                      viewBox="0 0 74 60"
                      fill="none"
                      preserveAspectRatio="none"
                    >
                      <defs>
                        <linearGradient
                          id={getArcGradientId()}
                          x1="37"
                          y1="59"
                          x2="37"
                          y2="0"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop
                            offset="0"
                            stopColor={getColor()}
                            stopOpacity="0.4"
                          />
                          <stop
                            offset="1"
                            stopColor="#151D2C"
                            stopOpacity="0.36"
                          />
                        </linearGradient>
                      </defs>
                      <path
                        d="M74 0H0V59C11 39.7 27.2 24.1 46.9 13.9L74 0Z"
                        fill={`url(#${getArcGradientId()})`}
                      />
                    </svg>
                  </div>
                )}
              </div>
              <div className="absolute inset-0">{provided.placeholder}</div>
            </div>
          );
        }}
      </Droppable>
    );
  };

  return (
    <div
      data-drag-container
      className={cn(
        "fixed z-[200] bottom-[11px] pr-10 h-[7rem] max-[1400px]:h-[149px] flex justify-center items-end gap-0.5 transition-opacity duration-300",
        isSidebarOpen ? "w-[calc(100%-208px)]" : "w-[calc(100%-52px)]",
        isDragging
          ? "opacity-100 pointer-events-auto"
          : "opacity-0 pointer-events-none"
      )}
    >
      {map([LOST_DEAL, WON_DEAL], (areaId: wonOrLostType) =>
        renderArea(areaId)
      )}
    </div>
  );
};

export default DragDropAreas;
